import React, { useState, useMemo } from 'react'
import { useMutation, useQuery } from '@apollo/react-hooks'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input as ShadcnInput } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Send,
  Plus,
  FileText,
  Mail,
  FolderOpen,
  X,
  User,
  ChevronDown,
  ChevronUp,
  LoaderCircle,
  Calendar,
  Hash,
  Lock,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toastr } from 'react-redux-toastr'
import * as Mutation from '../../../api/gql/mutation'
import * as Query from '../../../api/gql/query'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import { formatBytes } from '../../../utils/formatters'
import MemberCheckboxPicker from '../../../components/MemberCheckboxPicker'
import DocumentSelectorModal from './DocumentSelectorModal'
import DatePicker from 'react-datepicker'
import * as lngs from 'date-fns/locale'
import i18n from '../../../i18n'
import 'react-datepicker/dist/react-datepicker.css'

interface QuickTransmittalModalProps {
  isOpen: boolean
  onClose: (updated?: boolean) => void
  transmittalId?: string | null // If provided, opens in view/edit mode
  initialDocuments?: SelectedDocument[] // Pre-fill the document list (e.g. "Send as Transmittal" from the browser)
}

export interface SelectedDocument {
  id: string
  name: string
  currentRevision?: {
    id: string
    version: number
    file?: {
      id: string
      filename: string
      size: number
      mimetype: string
    }
  }
}

interface Recipient {
  type: 'internal' | 'external'
  userId?: string
  user?: { id: string; name: string; email: string }
  email?: string
}

interface ProjectUser {
  id: string
  role: string
  user: {
    id: string
    name: string
    email: string
  }
}

const QuickTransmittalModal: React.FC<QuickTransmittalModalProps> = ({
  isOpen,
  onClose,
  transmittalId,
  initialDocuments,
}) => {
  const { t } = useTranslation()
  const isEditMode = !!transmittalId

  // Form state
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [reference, setReference] = useState('')
  const [confidential, setConfidential] = useState(false)
  const [expiresAt, setExpiresAt] = useState<Date | null>(null)
  const [documents, setDocuments] = useState<SelectedDocument[]>(initialDocuments || [])
  const [recipients, setRecipients] = useState<Recipient[]>([])
  const [newEmail, setNewEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [documentSelectorOpen, setDocumentSelectorOpen] = useState(false)
  const [showAllDocuments, setShowAllDocuments] = useState(false)

  // Fetch current project
  const { data: meData } = useQuery(Query.ME)
  const currentProject = meData?.me?.currentProject
  const projectId = currentProject?.id

  // canEdit gates the edit-mode Save (updateDocTransmittal → DOC_CONTROL.EDIT).
  // The modal is reachable in view mode by anyone with VIEW via row click,
  // so the Save trigger inside must be gated here rather than at the call site.
  const { canEdit } = useModuleActions('DOC_CONTROL', projectId)

  // Fetch project members
  const { data: projectData } = useQuery(Query.PROJECT, {
    variables: { id: projectId },
    skip: !projectId,
  })
  const projectUsers: ProjectUser[] = projectData?.project?.users || []

  // Fetch existing transmittal when in edit mode
  const { data: transmittalData, loading: loadingTransmittal } = useQuery(Query.DOC_TRANSMITTAL, {
    variables: { id: transmittalId },
    skip: !transmittalId,
    fetchPolicy: 'network-only',
  })

  // Sent (or expired) transmittals are immutable — view only
  const isReadOnly =
    isEditMode &&
    !!transmittalData?.docTransmittal &&
    transmittalData.docTransmittal.status !== 'DRAFT'

  // Populate form when transmittal data loads
  React.useEffect(() => {
    if (transmittalData?.docTransmittal) {
      const t = transmittalData.docTransmittal
      setName(t.name || '')
      setDescription(t.description || '')
      setReference(t.reference || '')
      setConfidential(!!t.confidential)
      setExpiresAt(t.expiresAt ? new Date(t.expiresAt) : null)
    }
  }, [transmittalData])

  // Create and send mutation
  const [createAndSend, { loading: sending }] = useMutation(
    Mutation.CREATE_AND_SEND_DOC_TRANSMITTAL,
    {
      onCompleted: () => {
        toastr.success('', t('DocControl_transmittal_sent'))
        onClose(true)
      },
      onError: (error) => {
        toastr.error(t('Common_error'), error.message)
      },
    },
  )

  // Update mutation for edit mode
  const [updateTransmittal, { loading: updating }] = useMutation(Mutation.UPDATE_DOC_TRANSMITTAL, {
    onCompleted: () => {
      toastr.success('', t('DocControl_transmittal_updated'))
      onClose(true)
    },
    onError: (error) => {
      toastr.error(t('Common_error'), error.message)
    },
  })

  // Validation
  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const canSend = useMemo(() => {
    if (isEditMode) {
      return name.trim().length > 0
    }
    return name.trim().length > 0 && documents.length > 0 && recipients.length > 0
  }, [name, documents, recipients, isEditMode])

  // Set of currently selected internal user ids (for checkbox state)
  const selectedUserIds = useMemo(
    () => new Set(recipients.filter((r) => r.type === 'internal').map((r) => r.userId)),
    [recipients],
  )

  const allMembersSelected =
    projectUsers.length > 0 && projectUsers.every((pu) => selectedUserIds.has(pu.user.id))

  // External recipients only (internal ones are reflected by their checkbox)
  const externalRecipients = useMemo(
    () => recipients.filter((r) => r.type === 'external'),
    [recipients],
  )

  // Check if an email is already added (as external or matches an internal user)
  const isEmailAlreadyAdded = (email: string): boolean => {
    const normalizedEmail = email.toLowerCase()
    if (
      recipients.some((r) => r.type === 'external' && r.email?.toLowerCase() === normalizedEmail)
    ) {
      return true
    }
    if (
      recipients.some(
        (r) => r.type === 'internal' && r.user?.email?.toLowerCase() === normalizedEmail,
      )
    ) {
      return true
    }
    return false
  }

  // Handlers
  const handleToggleMember = (userId: string) => {
    if (selectedUserIds.has(userId)) {
      setRecipients(recipients.filter((r) => !(r.type === 'internal' && r.userId === userId)))
      return
    }

    const pu = projectUsers.find((p) => p.user.id === userId)
    if (!pu) return

    // Adding a member supersedes any matching external email (avoid duplicate sends)
    const email = pu.user.email.toLowerCase()
    setRecipients([
      ...recipients.filter((r) => !(r.type === 'external' && r.email?.toLowerCase() === email)),
      { type: 'internal', userId: pu.user.id, user: pu.user },
    ])
  }

  const handleToggleAllMembers = () => {
    if (allMembersSelected) {
      setRecipients(recipients.filter((r) => r.type !== 'internal'))
      return
    }
    const internal: Recipient[] = projectUsers.map((pu) => ({
      type: 'internal',
      userId: pu.user.id,
      user: pu.user,
    }))
    const memberEmails = new Set(projectUsers.map((pu) => pu.user.email.toLowerCase()))
    const externals = recipients.filter(
      (r) => r.type === 'external' && !memberEmails.has(r.email?.toLowerCase() || ''),
    )
    setRecipients([...internal, ...externals])
  }

  const handleAddEmail = () => {
    const email = newEmail.trim().toLowerCase()
    if (!email) return

    if (!isValidEmail(email)) {
      setEmailError(t('Common_invalid_email'))
      return
    }

    if (isEmailAlreadyAdded(email)) {
      setEmailError(t('DocControl_recipient_already_added'))
      return
    }

    setRecipients([
      ...recipients,
      {
        type: 'external',
        email,
      },
    ])
    setNewEmail('')
    setEmailError('')
  }

  const handleRemoveExternal = (email?: string) => {
    setRecipients(recipients.filter((r) => !(r.type === 'external' && r.email === email)))
  }

  const handleAddDocuments = (docs: SelectedDocument[]) => {
    const existingIds = new Set(documents.map((d) => d.id))
    const newDocs = docs.filter((d) => !existingIds.has(d.id))
    setDocuments([...documents, ...newDocs])
  }

  const handleRemoveDocument = (docId: string) => {
    setDocuments(documents.filter((d) => d.id !== docId))
  }

  const handleSend = async () => {
    if (!canSend) return

    await createAndSend({
      variables: {
        input: {
          name: name.trim(),
          description: description.trim() || null,
          confidential,
          expiresAt: expiresAt ? expiresAt.toISOString() : null,
          documentIds: documents.map((d) => d.id),
          recipients: recipients.map((r) =>
            r.type === 'internal' ? { userId: r.userId } : { email: r.email },
          ),
        },
      },
    })
  }

  const handleUpdate = async () => {
    if (!canSend || !transmittalId || isReadOnly) return

    await updateTransmittal({
      variables: {
        id: transmittalId,
        input: {
          name: name.trim(),
          description: description.trim() || null,
          confidential,
          expiresAt: expiresAt ? expiresAt.toISOString() : null,
        },
      },
    })
  }

  const handleClose = () => {
    onClose(false)
  }

  const existingDocumentIds = useMemo(() => documents.map((d) => d.id), [documents])

  // Document rows for both create and edit modes
  const renderDocumentRow = (doc: { id: string; name: string; version?: number; size?: number }, index: number, removable: boolean) => (
    <tr key={doc.id} className="border-b last:border-b-0">
      <td className="py-1.5 px-3">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="truncate text-sm">{doc.name}</span>
        </div>
      </td>
      <td className="py-1.5 px-3 text-center">
        {doc.version != null && (
          <Badge variant="secondary" className="text-xs">
            v{doc.version}
          </Badge>
        )}
      </td>
      <td className="py-1.5 px-3 text-right text-xs text-muted-foreground whitespace-nowrap">
        {doc.size ? formatBytes(doc.size) : '—'}
      </td>
      {removable && (
        <td className="py-1.5 px-1 w-8">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
            onClick={() => handleRemoveDocument(doc.id)}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </td>
      )}
    </tr>
  )

  const editDocRows = (transmittalData?.docTransmittal?.items || []).map((item: any) => ({
    id: item.id,
    name: item.document?.name,
    version: item.revision?.version,
    size: item.revision?.file?.size,
  }))

  const createDocRows = (showAllDocuments ? documents : documents.slice(0, 5)).map((doc) => ({
    id: doc.id,
    name: doc.name,
    version: doc.currentRevision?.version,
    size: doc.currentRevision?.file?.size,
  }))

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col gap-0 p-0">
          <DialogHeader className="px-5 py-4 border-b">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Send className="h-4 w-4 text-primary" />
              {isEditMode
                ? isReadOnly
                  ? t('DocControl_view_transmittal')
                  : t('DocControl_edit_transmittal')
                : t('DocControl_send_transmittal')}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            {isEditMode && loadingTransmittal ? (
              <div className="flex items-center justify-center py-10">
                <LoaderCircle className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
            <div className="space-y-5">
              {/* Form Fields */}
              <div className="grid grid-cols-1 md:grid-cols-6 gap-x-4 gap-y-3">
                <div className="md:col-span-4 space-y-1.5">
                  <Label className="text-xs">{t('DocControl_transmittal_name')} *</Label>
                  <ShadcnInput
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('DocControl_transmittal_name_placeholder')}
                    className="h-9"
                    autoFocus={!isReadOnly}
                    disabled={isReadOnly}
                  />
                </div>
                <div className="md:col-span-2 space-y-1.5">
                  <Label className="text-xs flex items-center gap-1">
                    <Hash className="h-3 w-3" />
                    {t('DocControl_reference')}
                  </Label>
                  <div className="h-9 flex items-center">
                    {reference ? (
                      <Badge variant="secondary" className="font-mono">{reference}</Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground">{t('DocControl_reference_auto')}</span>
                    )}
                  </div>
                </div>
                <div className="md:col-span-4 space-y-1.5">
                  <Label className="text-xs">{t('DocControl_description')}</Label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t('DocControl_transmittal_description_placeholder')}
                    rows={2}
                    className="resize-none"
                    disabled={isReadOnly}
                  />
                </div>
                <div className="md:col-span-2 space-y-1.5">
                  <Label className="text-xs flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {t('DocControl_expires_at')}
                  </Label>
                  <DatePicker
                    selected={expiresAt}
                    onChange={(date: Date | null) => setExpiresAt(date)}
                    dateFormat="yyyy/MM/dd"
                    className="flex h-9 w-full rounded-md border border-input bg-card px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    wrapperClassName="w-full"
                    locale={(lngs as any)[i18n.language]}
                    isClearable={!isReadOnly}
                    placeholderText={t('DocControl_expires_at_help')}
                    minDate={new Date()}
                    disabled={isReadOnly}
                  />
                </div>
                <div className="md:col-span-2 flex items-center gap-3 pt-1">
                  <Switch
                    checked={confidential}
                    onCheckedChange={setConfidential}
                    disabled={isReadOnly}
                  />
                  <div>
                    <span className="text-sm flex items-center gap-1.5">
                      <Lock className="h-3.5 w-3.5" />
                      {t('DocControl_confidential', 'Confidential')}
                    </span>
                    <p className="text-xs text-muted-foreground">
                      {t('DocControl_confidential_desc', 'Only the sender and recipients can see this transmittal')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Documents Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('DocControl_documents')}
                    <Badge variant="secondary" className="ml-2 text-[10px] px-1.5 py-0 font-normal">
                      {isEditMode ? (transmittalData?.docTransmittal?.items?.length || 0) : documents.length}
                    </Badge>
                  </Label>
                  {!isEditMode && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => setDocumentSelectorOpen(true)}
                    >
                      <FolderOpen className="h-3.5 w-3.5" />
                      {t('DocControl_add_documents')}
                    </Button>
                  )}
                </div>

                {isEditMode ? (
                  editDocRows.length > 0 ? (
                    <div className="border rounded-md overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="py-1.5 px-3 text-left text-xs font-medium text-muted-foreground">{t('Common_name')}</th>
                            <th className="py-1.5 px-3 text-center text-xs font-medium text-muted-foreground w-16">{t('DocControl_version')}</th>
                            <th className="py-1.5 px-3 text-right text-xs font-medium text-muted-foreground w-20">{t('DocControl_size')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {editDocRows.map((doc: any, index: number) => renderDocumentRow(doc, index, false))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="border rounded-md bg-muted/30 text-center text-muted-foreground py-4 text-sm">
                      <FileText className="h-4 w-4 mr-1.5 inline-block" />
                      {t('DocControl_no_documents_in_transmittal')}
                    </div>
                  )
                ) : documents.length === 0 ? (
                  <div className="border rounded-md bg-muted/30 text-center text-muted-foreground py-4 text-sm">
                    <FileText className="h-4 w-4 mr-1.5 inline-block" />
                    {t('DocControl_no_documents_in_transmittal')}
                  </div>
                ) : (
                  <div className="border rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="py-1.5 px-3 text-left text-xs font-medium text-muted-foreground">{t('Common_name')}</th>
                          <th className="py-1.5 px-3 text-center text-xs font-medium text-muted-foreground w-16">{t('DocControl_version')}</th>
                          <th className="py-1.5 px-3 text-right text-xs font-medium text-muted-foreground w-20">{t('DocControl_size')}</th>
                          <th className="w-8" />
                        </tr>
                      </thead>
                      <tbody>
                        {createDocRows.map((doc, index) => renderDocumentRow(doc, index, true))}
                      </tbody>
                    </table>
                    {documents.length > 5 && (
                      <div
                        className="text-center border-t py-1.5 cursor-pointer hover:bg-muted/30 transition-colors"
                        onClick={() => setShowAllDocuments(!showAllDocuments)}
                      >
                        <span className="text-xs text-primary inline-flex items-center gap-1">
                          {showAllDocuments ? (
                            <ChevronUp className="h-3 w-3" />
                          ) : (
                            <ChevronDown className="h-3 w-3" />
                          )}
                          {showAllDocuments
                            ? t('Common_show_less')
                            : t('Common_show_all', { count: documents.length })}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Recipients Section */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('DocControl_recipients')}
                  <Badge variant="secondary" className="ml-2 text-[10px] px-1.5 py-0 font-normal">
                    {isEditMode ? (transmittalData?.docTransmittal?.recipients?.length || 0) : recipients.length}
                  </Badge>
                </Label>

                {isEditMode ? (
                  <div className="flex flex-wrap gap-1.5">
                    {(transmittalData?.docTransmittal?.recipients || []).map((recipient: any) => (
                      <Badge
                        key={recipient.id}
                        variant={recipient.user ? 'default' : 'secondary'}
                        className="flex items-center py-1 px-2.5 text-xs"
                      >
                        {recipient.user ? (
                          <User className="h-3 w-3 mr-1" />
                        ) : (
                          <Mail className="h-3 w-3 mr-1" />
                        )}
                        {recipient.user ? recipient.user.name : recipient.email}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Project members — multi-select checkbox list */}
                    <MemberCheckboxPicker
                      members={projectUsers.map((pu) => ({
                        id: pu.user.id,
                        name: pu.user.name,
                        email: pu.user.email,
                        role: pu.role,
                      }))}
                      selectedIds={selectedUserIds}
                      onToggle={handleToggleMember}
                      onToggleAll={handleToggleAllMembers}
                      allSelected={allMembersSelected}
                      label={t('DocControl_project_members')}
                      emptyLabel={t('DocControl_no_members')}
                      showRoleBadge
                      showCounter
                    />

                    {/* External email recipients */}
                    <div className="space-y-1">
                      <label className="text-[11px] text-muted-foreground flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {t('DocControl_external_email')}
                      </label>
                      <div className="flex gap-2">
                        <ShadcnInput
                          type="email"
                          placeholder={t('DocControl_recipient_email_placeholder')}
                          value={newEmail}
                          onChange={(e) => {
                            setNewEmail(e.target.value)
                            setEmailError('')
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              handleAddEmail()
                            }
                          }}
                          className={`h-9 ${emailError ? 'border-destructive' : ''}`}
                        />
                        <Button size="sm" className="h-9 shrink-0" onClick={handleAddEmail}>
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      {emailError && <p className="text-destructive text-xs">{emailError}</p>}
                      {externalRecipients.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-1.5">
                          {externalRecipients.map((recipient) => (
                            <Badge
                              key={recipient.email}
                              variant="secondary"
                              className="flex items-center py-1 px-2.5 text-xs"
                            >
                              <Mail className="h-3 w-3 mr-1" />
                              {recipient.email}
                              <button
                                type="button"
                                className="ml-1 opacity-70 hover:opacity-100"
                                onClick={() => handleRemoveExternal(recipient.email)}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t">
            <Button variant="outline" size="sm" onClick={handleClose} disabled={sending || updating}>
              {isEditMode ? t('Common_close') : t('Common_cancel')}
            </Button>
            {isEditMode ? (
              !isReadOnly && canEdit && (
                <Button size="sm" onClick={handleUpdate} disabled={!canSend || updating}>
                  {updating ? <LoaderCircle className="h-4 w-4 animate-spin" /> : t('Common_save')}
                </Button>
              )
            ) : (
              <Button size="sm" onClick={handleSend} disabled={!canSend || sending}>
                {sending ? (
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    {t('DocControl_send')}
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Selector Modal */}
      {documentSelectorOpen && (
        <DocumentSelectorModal
          isOpen={documentSelectorOpen}
          onClose={() => setDocumentSelectorOpen(false)}
          onSelect={handleAddDocuments}
          excludeDocumentIds={existingDocumentIds}
          projectId={projectId}
        />
      )}
    </>
  )
}

export default QuickTransmittalModal
